































































































































































































































































import gql from 'graphql-tag';
import Component from 'vue-class-component';

import { Org } from '@/api-svc-types';
import { BaseVue } from '@/BaseVue';
import { stringifyError } from '@/utils/error';

import AppLink from '../AppLink.vue';
import UiAlert from '../ui/UiAlert.vue';
import UiButton from '../ui/UiButton.vue';
import UiDatePicker from '../ui/UiDatePicker.vue';
import UiModal from '../ui/UiModal.vue';

function extractHardCloseTransactionIds(message = ''): string[] {
  const txnsMatch = message.match(/Txns:\s*(\[[\s\S]*?\])/);
  if (!txnsMatch?.[1]) return [];

  try {
    const parsed = JSON.parse(txnsMatch[1]);
    return Array.isArray(parsed)
      ? parsed.filter((transactionId): transactionId is string => typeof transactionId === 'string' && !!transactionId)
      : [];
  } catch {
    return txnsMatch[1]
      .replace(/^\[/, '')
      .replace(/\]$/, '')
      .split(',')
      .map((transactionId) => transactionId.trim().replace(/^["']|["']$/g, ''))
      .filter(Boolean);
  }
}

function formatHardCloseErrorMessage(message = ''): string {
  if (message.includes('Cannot lock period with uncategorised and/or unignored transactions.')) {
    return 'Cannot lock period with uncategorised transactions.';
  }
  return message.replace(/\s*Txns:\s*\[[\s\S]*?\]\s*/g, '').trim();
}

const HARD_CLOSE_LOADING_MESSAGES = [
  'Loading transactions...',
  'Scanning transaction categorization...',
  'Checking for unresolved transactions...',
  'Running validation checks...',
  'Thank you for your patience...',
  'This is taking a little longer than usual...',
  'Almost there...',
];

async function copyTextToClipboard(text: string) {
  const policy = (document as any).permissionsPolicy ?? (document as any).featurePolicy;
  const clipboardWriteAllowed = !policy?.allowsFeature || policy.allowsFeature('clipboard-write');

  try {
    if (clipboardWriteAllowed && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }
  } catch (clipboardError) {
    console.debug('Clipboard API unavailable, falling back to document copy command', clipboardError);
  }

  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.style.position = 'fixed';
  textArea.style.left = '-9999px';
  textArea.style.top = '0';
  textArea.setAttribute('readonly', '');
  document.body.appendChild(textArea);
  textArea.select();

  try {
    const copied = document.execCommand('copy');
    if (!copied) throw new Error('document.execCommand("copy") returned false');
  } finally {
    document.body.removeChild(textArea);
  }
}

@Component({
  components: {
    UiAlert,
    UiModal,
    UiDatePicker,
    UiButton,
    AppLink,
  },
})
export default class PeriodCloseFirst extends BaseVue {
  public get currentOrg(): Org {
    return this.$store.state.currentOrg;
  }

  public hardCloseEditDateOpen = false;
  public newHardCloseDate: string | null = null;
  public hardCloseLoading = false;
  public hardCloseWarningOpen = false;
  public hardCloseErrorMessage: string | null = null;
  public hardCloseTransactionIds: string[] = [];
  public hardCloseLoadingMessageIndex = 0;
  private hardCloseLoadingMessageInterval: number | null = null;

  public hardCloseAuditIcons = {
    set: { icon: 'fa-solid fa-lock', color: 'tw-text-green-500' },
    unset: { icon: 'fa-solid fa-unlock', color: 'tw-text-red-500' },
    update: { icon: 'fa-solid fa-pencil', color: 'tw-text-blue-500' },
  };

  public get currentHardCloseLoadingMessage() {
    return this.hardCloseLoading ? HARD_CLOSE_LOADING_MESSAGES[this.hardCloseLoadingMessageIndex] : undefined;
  }

  beforeDestroy() {
    this.stopHardCloseLoadingMessages();
  }

  localeFormatDate(date: string) {
    const d = new Date(date); // this will create the date in UTC
    const regionalDate = new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()); // this stage will convert the date to the local timezone
    return regionalDate.toLocaleDateString(); // this will format the date to the locale
  }

  hardCloseOn() {
    this.newHardCloseDate = null;
    this.hardCloseEditDateOpen = true;
  }

  public async hardCloseOff() {
    await this.updateHardClose(null);
  }

  hardCloseEdit() {
    this.newHardCloseDate = this.currentOrg.hardCloseDate ?? null;
    this.hardCloseEditDateOpen = true;
  }

  setHardCloseError(message: string) {
    this.hardCloseErrorMessage = formatHardCloseErrorMessage(message);
    this.hardCloseTransactionIds = extractHardCloseTransactionIds(message);
  }

  clearHardCloseError() {
    this.hardCloseErrorMessage = null;
    this.hardCloseTransactionIds = [];
  }

  startHardCloseLoadingMessages() {
    this.stopHardCloseLoadingMessages();
    this.hardCloseLoadingMessageIndex = 0;
    this.hardCloseLoadingMessageInterval = window.setInterval(() => {
      const nextMessageIndex = this.hardCloseLoadingMessageIndex + 1;
      if (nextMessageIndex >= HARD_CLOSE_LOADING_MESSAGES.length - 1) {
        this.hardCloseLoadingMessageIndex = HARD_CLOSE_LOADING_MESSAGES.length - 1;
        this.stopHardCloseLoadingMessages();
        return;
      }
      this.hardCloseLoadingMessageIndex = nextMessageIndex;
    }, 7000);
  }

  stopHardCloseLoadingMessages() {
    if (this.hardCloseLoadingMessageInterval !== null) {
      window.clearInterval(this.hardCloseLoadingMessageInterval);
      this.hardCloseLoadingMessageInterval = null;
    }
  }

  openTransactionV2(transactionId: string) {
    const parentUrl = process.env.VUE_APP_PARENT_WEB_APP ?? window.location.origin;
    const url = new URL('/transactionsv2', parentUrl);
    url.searchParams.set('id', transactionId);
    window.open(url.toString(), '_blank', 'noopener,noreferrer');
  }

  async copyTransactionId(transactionId: string) {
    try {
      await copyTextToClipboard(transactionId);
      this.showSuccessSnackbar('Transaction ID copied to clipboard');
    } catch (error) {
      console.error('copyTransactionId error', error);
      this.showErrorSnackbar('Failed to copy transaction ID');
    }
  }

  async updateHardClose(date: string | null) {
    this.hardCloseLoading = true;
    this.startHardCloseLoadingMessages();
    this.clearHardCloseError();
    try {
      const resp = await this.$apollo.mutate({
        mutation: gql`
          mutation updateOrg($orgId: ID!, $hardCloseDate: String!) {
            updateOrg(orgId: $orgId, hardCloseDate: $hardCloseDate) {
              id
            }
          }
        `,
        variables: {
          orgId: this.currentOrg.id,
          hardCloseDate: date === null ? '' : date,
        },
      });
      if (resp.errors) {
        const message = resp.errors.map((error) => error.message ?? String(error)).join('\n');
        this.setHardCloseError(message);
        return;
      }
      await Promise.all([this.$store.dispatch('updateOrgs')]);
    } catch (e) {
      const message = stringifyError(e, { hideErrorName: true }).replace('GraphQL error: ', '');
      this.setHardCloseError(message);
      console.error(e);
    } finally {
      this.hardCloseLoading = false;
      this.stopHardCloseLoadingMessages();
    }
  }
}
